import React, { useState } from 'react';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
export function ChangePasswordScreen({
  onBack
}) {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear confirm password match error if needed
    if (name === 'newPassword' || name === 'confirmPassword') {
      if (errors.confirmPassword && errors.confirmPassword.includes('match')) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
  };
  const togglePasswordVisibility = field => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    // Validate current password
    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
      valid = false;
    }
    // Validate new password
    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
      valid = false;
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
      valid = false;
    }
    // Validate confirm password
    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
      valid = false;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // In a real app, this would call an API to update the password
      console.log('Password updated successfully');
      onBack();
    }
  };
  return <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-38">
      <div className="flex items-center p-4" data-id="element-39">
        <button onClick={onBack} className="p-1 rounded-full hover:bg-gray-100" aria-label="Back" data-id="element-40">
          <ArrowLeftIcon className="w-6 h-6 text-gray-500" data-id="element-41" />
        </button>
        <h2 className="ml-4 text-xl font-semibold" data-id="element-42">Change Password</h2>
      </div>
      <form onSubmit={handleSubmit} className="flex-1 px-4 py-6 space-y-6" data-id="element-43">
        {/* Current Password */}
        <div className="space-y-1" data-id="element-44">
          <div className="relative" data-id="element-45">
            <input type={passwordVisibility.currentPassword ? 'text' : 'password'} id="currentPassword" name="currentPassword" value={passwordData.currentPassword} onChange={handleChange} placeholder="Current password" className={`w-full p-4 border ${errors.currentPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-46" />
            <button type="button" onClick={() => togglePasswordVisibility('currentPassword')} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500" aria-label={passwordVisibility.currentPassword ? 'Hide password' : 'Show password'} data-id="element-47">
              {passwordVisibility.currentPassword ? <EyeIcon className="w-5 h-5" data-id="element-48" /> : <EyeOffIcon className="w-5 h-5" data-id="element-49" />}
            </button>
          </div>
          {errors.currentPassword && <p className="text-sm text-red-600" data-id="element-50">{errors.currentPassword}</p>}
        </div>
        {/* New Password */}
        <div className="space-y-1" data-id="element-51">
          <div className="relative" data-id="element-52">
            <input type={passwordVisibility.newPassword ? 'text' : 'password'} id="newPassword" name="newPassword" value={passwordData.newPassword} onChange={handleChange} placeholder="New password" className={`w-full p-4 border ${errors.newPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-53" />
            <button type="button" onClick={() => togglePasswordVisibility('newPassword')} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500" aria-label={passwordVisibility.newPassword ? 'Hide password' : 'Show password'} data-id="element-54">
              {passwordVisibility.newPassword ? <EyeIcon className="w-5 h-5" data-id="element-55" /> : <EyeOffIcon className="w-5 h-5" data-id="element-56" />}
            </button>
          </div>
          <p className={`text-sm ${errors.newPassword ? 'text-red-600' : 'text-gray-500'}`} data-id="element-57">
            8 characters minimum
          </p>
          {errors.newPassword && errors.newPassword !== 'Password must be at least 8 characters' && <p className="text-sm text-red-600" data-id="element-58">{errors.newPassword}</p>}
        </div>
        {/* Confirm New Password */}
        <div className="space-y-1" data-id="element-59">
          <div className="relative" data-id="element-60">
            <input type={passwordVisibility.confirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={passwordData.confirmPassword} onChange={handleChange} placeholder="Confirm new password" className={`w-full p-4 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-61" />
            <button type="button" onClick={() => togglePasswordVisibility('confirmPassword')} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500" aria-label={passwordVisibility.confirmPassword ? 'Hide password' : 'Show password'} data-id="element-62">
              {passwordVisibility.confirmPassword ? <EyeIcon className="w-5 h-5" data-id="element-63" /> : <EyeOffIcon className="w-5 h-5" data-id="element-64" />}
            </button>
          </div>
          <p className={`text-sm ${errors.confirmPassword ? 'text-red-600' : 'text-gray-500'}`} data-id="element-65">
            8 characters minimum
          </p>
          {errors.confirmPassword && errors.confirmPassword !== 'Password must be at least 8 characters' && <p className="text-sm text-red-600" data-id="element-66">{errors.confirmPassword}</p>}
        </div>
        {/* Update Button */}
        <button type="submit" className="w-full py-4 mt-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors" data-id="element-67">
          Update password
        </button>
      </form>
    </div>;
}